/**
 * Determines the environment based on the second-level domain.
 * @param {string} sld - Second-level domain (e.g. 'ancestrydev', 'ancestrystage')
 * @returns {string} Environment name: 'dev', 'stage', or 'live'
 */
function getEnv(sld) {
	return {
		ancestrydev: 'dev',
		ancestrystage: 'stage'
	}[sld] || 'live';
}

/**
 * Parses a hostname into its subdomain, second-level domain, and TLD parts.
 * Assumes a single subdomain (which all Ancestry domains have).
 * @param {string} hostname - The full hostname (e.g. 'www.ancestry.com')
 * @returns {{subdomain: string, sld: string, tld: string}}
 */
function getDomainParts(hostname) {
	const [subdomain, sld, ...tldParts] = hostname.split('.');
	const tld = tldParts.join('.');
	return { subdomain, sld, tld };
}

/**
 * Resolves the current page locale from the document's lang attribute.
 * Falls back to 'en-us' if not set or unrecognized.
 * @returns {string} Lowercase locale string (e.g. 'en-us', 'de-de')
 */
function getLocale() {
	let lang =	(document.documentElement.lang || 'en-us').toLowerCase();
	if (lang.length <= 2) {
		const geos = [
			{ key: 'en', value: 'en-US' },
			// { key: 'en', value: 'en-GB' },
			// { key: 'en', value: 'en-CA' },
			{ key: 'es', value: 'es-MX' },
			// { key: 'es', value: 'es-ES' },
			// { key: 'es', value: 'es-AR' },
			{ key: 'fr', value: 'fr-FR' },
			// { key: 'fr', value: 'fr-CA' },
			{ key: 'de', value: 'de-DE' },
			// { key: 'de', value: 'de-AT' },
			{ key: 'it', value: 'it-IT' },
			{ key: 'nb', value: 'nb-NB' },
			{ key: 'nl', value: 'nl-NL' },
			{ key: 'pl', value: 'pl-PL' },
			{ key: 'pt', value: 'pt-PT' },
			{ key: 'sv', value: 'sv-SV' }
		];
		lang = (geos.find(geo => geo.key === lang)?.value || 'en-us').toLowerCase();
	}
	return lang;
}

/**
 * Checks if the URL indicates a previous eval flow state should be loaded.
 * @returns {boolean}
 */
function getLoadPreviousState() {
	return window.ui.getUrlParameterValue('evalflow') === 'true';
}

/**
 * Extracts the CDN URL override from the cocoLocal cookie value.
 * @param {string} cookie - The cocoLocal cookie string
 * @returns {string} The override CDN URL, or empty string if not found
 */
function getCdnUrlFromCocoLocalCookie(cookie) {
	const cocoActionStrings = cookie.split('|');
	let overrideCdnUrl = '';

	cocoActionStrings.forEach(string => {
		const [coockieActionName, value] = string.split('=');
		if (coockieActionName === 'Eval' && value) {
			const evalOverrideConfig = JSON.parse(value);
			const urlsObject = evalOverrideConfig?.loader?.urls;
			if (urlsObject) {
				const url = Object.keys(urlsObject)?.[0];
				overrideCdnUrl = url.split('/component.js')?.[0];
			}
		}
	});
	return overrideCdnUrl;
}

/**
 * Resolves the hintsui-eval CDN URL, checking for local overrides first.
 * @param {string} defaultCdnUrl - The default CDN base URL
 * @returns {string} The resolved CDN URL for hintsui-eval assets
 */
function getHintsUiEvalCdnUrl(defaultCdnUrl) {
	const cocoLocalCookie = window.ui.getCookie('cocoLocal');
	if (cocoLocalCookie?.includes('Eval')) {
		const overrideCdnUrl = getCdnUrlFromCocoLocalCookie(cocoLocalCookie);
		if (overrideCdnUrl !== '') {
			return overrideCdnUrl;
		}
	}
	return EVAL_VERSION ? `${defaultCdnUrl}/hintsui-eval/${EVAL_VERSION}` : 'https://www.ancestrystage.com/hintsui-eval'; // eslint-disable-line no-undef
}

/**
 * Resolves the Ancestry UI CDN URL by inspecting loaded stylesheets.
 * @param {string} cdnUrl - The base CDN URL
 * @returns {string} The full UI CDN URL including version path
 */
function getUiCdnUrl(cdnUrl) {
	return `${cdnUrl}/ui/${((((document.querySelector(`link[href^="${cdnUrl}/ui/"][href$="core.css"]`) || {}).href || '').split('/ui/')[1] || '2.9.3').split('/css/')[0])}`;
}

/**
 * Builds the full configuration object for the Eval component.
 * @param {string} [renderer='sidebar'] - Renderer type ('sidebar' or 'custom')
 * @param {Object} [overrides] - Optional config overrides
 * @returns {Object} Complete configuration object with URLs, locale, and environment settings
 */
function getConfig(renderer = 'sidebar', overrides) {
	const cdnUrl = 'https://www.ancestrycdn.com';
	const { sld, tld } = getDomainParts(window.location?.hostname);
	const locale = getLocale();
	const env = getEnv(sld);
	const uiCdnUrl = getUiCdnUrl(cdnUrl);
	const ancestryUrl = window.location?.origin;
	const newspapersUrl = 'https://img.newspapers.com';
	const mediaSvcUrl = '/api/media/retrieval';
	const placePFXUrl = `https://placepfx.${sld}.com`;
	const personPageUrlPrefix = `${ancestryUrl}/family-tree/person`;
	const searchUrl = `https://search.${sld}.${tld}`;
	const hintsUiApiUrlPrefix = `${ancestryUrl}/api/hintsui-api`;
	const personHintsApiUrl = `${ancestryUrl}/hintsui-personhints/api`;
	const hintsUiEvalCdnUrl = getHintsUiEvalCdnUrl(cdnUrl);
	const loadPreviousState = getLoadPreviousState();
	const config = {
		ancestryUrl,
		cdnUrl,
		newspapersUrl,
		uiCdnUrl,
		hintsUiEvalCdnUrl,
		env,
		hintsUiApiUrlPrefix,
		locale,
		loadPreviousState,
		mediaSvcUrl,
		placePFXUrl,
		personPageUrlPrefix,
		personHintsApiUrl,
		searchUrl,
		renderer
	};

	return overrides ? Object.assign(config, overrides) : config;
}

export default getConfig;
